import { Link } from "react-router-dom";
import { Award, GraduationCap, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings, mediaUrl, pickText } from "@/lib/siteSettings";

const DEFAULT_PORTRAIT = "/tammy-headshot.jpg";

const credentials = [
  "Associate Broker — 27 years",
  "Master Practitioner of Neurolinguistic Programming (NLP)",
  "Certified Personal & Professional Coach",
  "Certified Mental Emotional Release® Coach",
  "KWRI MAPS Leadership Coach",
  "Certified Personal Development & Executive Coach",
];

const focusAreas = [
  "Residential Real Estate",
  "Buyer & Seller Representation",
  "Business Coaching",
  "Productivity & Accountability",
  "Leadership Development",
  "Real Estate Agent Training",
  "Brand Clarity & Marketing Strategy",
  "Personal Growth & Mindset",
  "Team Development",
];

export default function About() {
  const { settings } = useSiteSettings();
  const portrait = mediaUrl(settings?.images?.portraitKey, DEFAULT_PORTRAIT);
  const signature = pickText(settings, "about.signature", "Be the person who stays.");
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="about-page">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        {/* Portrait */}
        <div className="md:col-span-5 md:sticky md:top-28">
          <div className="relative">
            <img
              src={portrait}
              alt="Tammy Richard — Real Estate Advisor & Coach"
              className="rounded-2xl w-full object-cover object-top aspect-[4/5] bg-[#FFFFFF]"
              data-testid="about-portrait"
            />
            <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">
              27 Years
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="md:col-span-6 md:col-start-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Meet Tammy
          </span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]" data-testid="about-title">
            Real Estate Advisor
            <br />
            & <span className="italic text-primary">Coach.</span>
          </h1>
          <p className="text-foreground/60 text-xs mt-5 uppercase tracking-[0.22em]">
            Clarity · Confidence · Purpose
          </p>

          <div className="mt-10 space-y-6 text-lg leading-relaxed text-foreground/85" data-testid="about-bio">
            <p>
              I've spent my career helping people make better decisions,
              build stronger businesses, and create lives that align with
              what matters most.
            </p>
            <p>
              Today, I bring together two complementary professions:{" "}
              <strong className="text-foreground">Real Estate Advisor</strong>{" "}
              and{" "}
              <strong className="text-foreground">
                Business &amp; Personal Performance Coach.
              </strong>
            </p>
            <p>
              As a REALTOR®, I help buyers, sellers, and investors navigate
              one of life's biggest financial and personal decisions with
              confidence, strategy, and clear communication. My goal is to
              simplify the process, protect my clients' interests, and
              provide guidance they can trust long after the closing table.
            </p>
            <p>
              As a coach, I work with professionals, entrepreneurs, and real
              estate agents who want greater clarity, consistency, and
              meaningful growth. My coaching combines practical business
              strategies with mindset development, drawing on my experience
              as a Certified Coach, Master Practitioner of Neuro-Linguistic
              Programming (NLP), and Mental Emotional Release® Practitioner.
            </p>
            <p>
              Throughout my career, I've coached hundreds of professionals
              in leadership, productivity, goal achievement, and business
              development. Whether I'm helping an agent grow their
              production, guiding a client through a real estate
              transaction, or supporting someone who's ready to rewrite
              limiting patterns, my mission remains the same:
            </p>
          </div>

          {/* Mission pull-quote */}
          <blockquote
            className="mt-8 border-l-2 border-primary pl-6 py-2"
            data-testid="about-mission"
          >
            <p className="font-serif text-2xl md:text-3xl leading-snug text-foreground">
              Help people move forward with clarity, confidence, and
              purpose.
            </p>
          </blockquote>

          <div className="mt-8 space-y-6 text-lg leading-relaxed text-foreground/85">
            <p>
              I'm also the founder of the{" "}
              <strong className="text-foreground">
                Cornerstone Advantage coaching platform,
              </strong>{" "}
              where I assist coaches on increasing their skills, and develop
              leaders to become skilled coaches.
            </p>
          </div>

          {/* Areas of Focus */}
          <div className="mt-12 border-t border-border pt-10" data-testid="about-focus-areas">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary mb-6">
              <Compass size={14} /> Areas of Focus
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {focusAreas.map((a, i) => (
                <li
                  key={a}
                  className="flex gap-2.5 items-start text-foreground/85"
                  data-testid={`focus-${i}`}
                >
                  <span className="text-primary mt-2 shrink-0 h-1 w-1 rounded-full bg-primary" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 space-y-6 text-lg leading-relaxed text-foreground/85">
            <p>
              I believe success isn't built by chasing perfection — it's
              built through consistent action, intentional choices, and
              staying committed to what matters most.
            </p>
            <p>
              If you're looking for a trusted real estate advisor, a coach
              who will challenge and encourage you, or simply someone who
              enjoys meaningful conversations about growth and leadership,
              I'd love to connect.
            </p>
          </div>

          {/* Signature */}
          <p
            className="font-serif italic text-3xl md:text-4xl text-primary mt-12"
            data-testid="about-signature"
          >
            {signature}
          </p>

          {/* Credentials */}
          <div className="mt-12 border-t border-border pt-10" data-testid="about-credentials">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary mb-6">
              <Award size={14} /> Credentials
            </div>
            <ul className="space-y-3">
              {credentials.map((c, i) => (
                <li key={c} className="flex gap-3 items-start" data-testid={`credential-${i}`}>
                  <GraduationCap className="text-primary shrink-0 mt-1" size={18} strokeWidth={1.6} />
                  <span className="text-foreground/85 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/booking" data-testid="about-book-btn">
              <Button className="rounded-full bg-primary hover:bg-primary/90 btn-lift px-8 h-12">
                Work with Tammy
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-contact-btn">
              <Button variant="outline" className="rounded-full px-8 h-12 border-border">
                Send a message
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
